
import StencilHTMLElement from "./stencil-element";

class WallpanelCalculator extends StencilHTMLElement {
  name = "WallpanelCalculator";
  
  // Constants
  DEFAULT_PANEL_WIDTH = 1200; // mm
  MIN_WIDTH = 1; // mm
  MAX_WIDTH = 100000; // mm (reasonable maximum)
  
  widthInput;
  quantityRequired;
  calculateButton;
  calculatorInputs;
  recalculateButton;
  calculatorCard;
  calculatorResult;
  resultCard;
  panelHeightInputs;
  panelHeightResults;
  
  // Bound methods for event listeners
  boundCalculatePanels;
  boundHideResultsPanel;
  boundHandleKeyPress;
  boundOnVariantUpdated;
  boundValidateInput;
  
  constructor() {
    super();
    
    // Bind methods once to avoid creating new functions on each call
    this.boundCalculatePanels = this.calculatePanels.bind(this);
    this.boundHideResultsPanel = this.hideResultsPanel.bind(this);
    this.boundHandleKeyPress = this.handleKeyPress.bind(this);
    this.boundOnVariantUpdated = this.onVariantUpdated.bind(this);
    this.boundValidateInput = this.validateInput.bind(this);
  }
  
  connectedCallback() {
    this.initializeElements();
    this.setupEventListeners();
    
    // Ensure result panel is hidden initially
    this.toggleCalculatorState(false);
    
    // Use requestAnimationFrame to ensure DOM is fully rendered before calculating heights
    requestAnimationFrame(() => {
      this.calculateHeights();
    });
  }
  
  disconnectedCallback() {
    // Clean up event listeners to prevent memory leaks
    if (this.calculateButton) {
      this.calculateButton.removeEventListener("click", this.boundCalculatePanels);
    }
    
    if (this.recalculateButton) {
      this.recalculateButton.removeEventListener("click", this.boundHideResultsPanel);
    }
    
    if (this.widthInput) {
      this.widthInput.removeEventListener("keypress", this.boundHandleKeyPress);
      this.widthInput.removeEventListener("input", this.boundValidateInput);
    }
    
    this.removeEventListener('wallpanel:variant-updated', this.boundOnVariantUpdated);
  }
  
  get isDebug() {
    try {
      // @ts-ignore - window properties may not be typed
      return (window.Stencil?.Settings?.debug) || (window.theme?.isDevelopment) || false;
    } catch {
      return false;
    }
  }
  
  log(...args) {
    if (this.isDebug) {
      console.log(this.logPrefix, ...args);
    }
  }

  initializeElements() {
    this.calculateButton = this.querySelector(".calculate-button");
    this.widthInput = this.querySelector(".width-input");
    this.quantityRequired = this.querySelector(".quantity-required");
    this.calculatorInputs = this.querySelector(".calculator-inputs");
    this.recalculateButton = this.querySelector(".recalculate-button");
    this.calculatorCard = this.querySelector(".calculator-card"); 
    this.calculatorResult = this.querySelector(".calculator-result");
    this.resultCard = this.querySelector(".result-panel");
  }

  setupEventListeners() {
    // Remove old listeners first to prevent duplicates
    if (this.calculateButton) {
      this.calculateButton.removeEventListener("click", this.boundCalculatePanels);
      // Add click listener - don't use capture phase to avoid interfering with Bootstrap
      this.calculateButton.addEventListener("click", this.boundCalculatePanels);
    }
    
    if (this.recalculateButton) {
      this.recalculateButton.removeEventListener("click", this.boundHideResultsPanel);
      this.recalculateButton.addEventListener("click", this.boundHideResultsPanel);
    }
    
    if (this.widthInput) {
      // Remove old listeners first
      this.widthInput.removeEventListener("keypress", this.boundHandleKeyPress);
      this.widthInput.removeEventListener("input", this.boundValidateInput);
      
      // Support Enter key for better UX
      this.widthInput.addEventListener("keypress", this.boundHandleKeyPress);
      // Real-time input validation
      this.widthInput.addEventListener("input", this.boundValidateInput);
    }
    
    // Listen for variant updates (remove first to prevent duplicates)
    this.removeEventListener('wallpanel:variant-updated', this.boundOnVariantUpdated);
    this.addEventListener('wallpanel:variant-updated', this.boundOnVariantUpdated);
  }
  
  handleKeyPress(event) {
    if (event.key === 'Enter' && this.widthInput?.value) {
      event.preventDefault();
      this.calculatePanels();
    }
  }
  
  validateInput(event) {
    const input = event.target;
    let value = input.value;
    
    // Remove non-numeric characters except decimal point
    value = value.replace(/[^0-9.]/g, '');
    
    // Prevent multiple decimal points
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }
    
    input.value = value;
    
    // Update ARIA attributes for accessibility
    const numValue = parseFloat(value);
    if (value && (isNaN(numValue) || numValue < this.MIN_WIDTH || numValue > this.MAX_WIDTH)) {
      input.setAttribute('aria-invalid', 'true');
      this.showError(`Please enter a valid width between ${this.MIN_WIDTH} and ${this.MAX_WIDTH} mm`);
    } else {
      input.setAttribute('aria-invalid', 'false');
      this.hideError();
    }
  }
  
  showError(message) {
    // Remove existing error if any
    this.hideError();
    
    const errorElement = document.createElement('div');
    errorElement.className = 'text-danger small mt-2';
    errorElement.id = 'width-input-error';
    errorElement.setAttribute('role', 'alert');
    errorElement.textContent = message;
    
    if (this.widthInput) {
      this.widthInput.setAttribute('aria-describedby', 'width-input-error');
      const parent = this.widthInput.parentElement;
      if (parent) {
        parent.appendChild(errorElement);
      }
    }
  }
  
  hideError() {
    const errorElement = this.querySelector('#width-input-error');
    if (errorElement) {
      errorElement.remove();
    }
    if (this.widthInput) {
      this.widthInput.removeAttribute('aria-describedby');
    }
  }

  calculateHeights() {
    if (this.calculatorCard && this.resultCard) {
      // Store current state
      const wasResultHidden = this.calculatorResult?.classList.contains('hidden');
      const wasInputHidden = this.calculatorInputs?.classList.contains('hidden');
      const currentHeight = this.calculatorCard.style.height;
      
      // Temporarily show input panel and hide result to measure input height
      if (this.calculatorInputs && this.calculatorResult) {
        this.calculatorInputs.classList.remove('hidden');
        this.calculatorInputs.classList.add('active');
        this.calculatorResult.classList.add('hidden');
        this.calculatorResult.classList.remove('active');
      }
      
      // Clear any fixed height to get natural height
      this.calculatorCard.style.height = '';
      
      // Force a reflow
      void this.calculatorCard.offsetHeight;
      
      // Measure input panel height
      this.panelHeightInputs = this.calculatorCard.offsetHeight;
      
      // Now temporarily show result panel and hide input to measure result height
      if (this.calculatorInputs && this.calculatorResult) {
        this.calculatorInputs.classList.add('hidden');
        this.calculatorInputs.classList.remove('active');
        this.calculatorResult.classList.remove('hidden');
        this.calculatorResult.classList.add('active');
      }
      
      // Force a reflow
      void this.calculatorCard.offsetHeight;
      
      // Measure result panel height
      this.panelHeightResults = this.calculatorCard.offsetHeight;
      
      // Restore original state
      if (this.calculatorInputs && this.calculatorResult) {
        if (wasInputHidden) {
          this.calculatorInputs.classList.add('hidden');
          this.calculatorInputs.classList.remove('active');
        } else {
          this.calculatorInputs.classList.remove('hidden');
          this.calculatorInputs.classList.add('active');
        }
        
        if (wasResultHidden) {
          this.calculatorResult.classList.add('hidden');
          this.calculatorResult.classList.remove('active');
        } else {
          this.calculatorResult.classList.remove('hidden');
          this.calculatorResult.classList.add('active');
        }
      }
      
      // Restore height if it was set
      if (currentHeight) {
        this.calculatorCard.style.height = currentHeight;
      }
      
      this.log('Heights calculated:', {
        panelHeightInputs: this.panelHeightInputs,
        panelHeightResults: this.panelHeightResults,
        currentCardHeight: this.calculatorCard.style.height
      });
    }
  }
  
  onVariantUpdated(event) {
    this.log('Variant updated, width value:', event.detail.widthValue);
    
    // Use double requestAnimationFrame to ensure DOM is fully updated after innerHTML replacement
    // The first RAF ensures the browser has processed the innerHTML change
    // The second RAF ensures any subsequent updates are complete
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Reinitialize all elements to get fresh references (button may have been replaced)
        this.initializeElements();
        
        // Verify we have the button with the correct data attribute
        if (this.calculateButton && event.detail.widthValue) {
          // Ensure the button has the correct data attribute
          const currentData = this.calculateButton.getAttribute('data-variant-wallpanel-width');
          if (currentData !== event.detail.widthValue) {
            this.calculateButton.setAttribute('data-variant-wallpanel-width', event.detail.widthValue);
            this.log('Updated button data attribute to:', event.detail.widthValue);
          }
        }
        
        // Setup event listeners on the new elements
        this.setupEventListeners();
        
        // Reset the calculator state
        this.resetCalculator();
        
        this.log('After variant update, elements found:', {
          calculateButton: !!this.calculateButton,
          widthInput: !!this.widthInput,
          quantityRequired: !!this.quantityRequired,
          panelHeightInputs: this.panelHeightInputs,
          panelHeightResults: this.panelHeightResults,
          buttonDataAttribute: this.calculateButton?.getAttribute('data-variant-wallpanel-width')
        });
      });
    });
  }

  resetCalculator() {
    this.log('Starting calculator reset...');
    
    // Clear input and reset to initial state
    if (this.widthInput) {
      this.widthInput.value = '';
      this.widthInput.setAttribute('aria-invalid', 'false');
    }
    
    this.hideError();
    
    if (this.quantityRequired) {
      this.quantityRequired.textContent = '0';
    }
    
    // Check if result panel is currently showing - if so, collapse smoothly
    const isResultShowing = this.calculatorResult && !this.calculatorResult.classList.contains('hidden');
    
    if (this.calculatorCard) {
      this.log('Before height reset:', {
        currentHeight: this.calculatorCard.style.height,
        panelHeightInputs: this.panelHeightInputs,
        cardOffsetHeight: this.calculatorCard.offsetHeight,
        isResultShowing
      });
      
      if (isResultShowing && this.calculatorCard.offsetHeight > 0) {
        // Result panel is showing - collapse smoothly
        const currentHeight = this.calculatorCard.offsetHeight;
        
        // Ensure we have the input panel height
        if (!this.panelHeightInputs) {
          // Temporarily show input panel to measure
          const wasInputHidden = this.calculatorInputs?.classList.contains('hidden');
          if (this.calculatorInputs) {
            this.calculatorInputs.classList.remove('hidden');
            this.calculatorInputs.classList.add('active');
          }
          
          // Hide result panel temporarily
          if (this.calculatorResult) {
            this.calculatorResult.classList.add('hidden');
            this.calculatorResult.classList.remove('active');
          }
          
          // Clear height and measure
          this.calculatorCard.style.height = '';
          void this.calculatorCard.offsetHeight; // Force reflow
          this.panelHeightInputs = this.calculatorCard.offsetHeight;
          
          // Restore state
          if (wasInputHidden && this.calculatorInputs) {
            this.calculatorInputs.classList.add('hidden');
            this.calculatorInputs.classList.remove('active');
          }
          if (this.calculatorResult) {
            this.calculatorResult.classList.remove('hidden');
            this.calculatorResult.classList.add('active');
          }
        }
        
        // Set current height for smooth transition
        this.calculatorCard.style.height = `${currentHeight}px`;
        void this.calculatorCard.offsetHeight; // Force reflow
        
        // Add transition and animate collapse
        const originalTransition = this.calculatorCard.style.transition;
        const originalMinHeight = this.calculatorCard.style.minHeight;
        this.calculatorCard.style.transition = 'height 0.3s ease-in-out';
        
        // Clear min-height if set, and set current height
        this.calculatorCard.style.minHeight = '';
        this.calculatorCard.style.height = `${currentHeight}px`;
        
        // Force a reflow
        void this.calculatorCard.offsetHeight;
        
        // Show input panel, hide result panel
        this.toggleCalculatorState(false);
        
        // Animate to input height
        requestAnimationFrame(() => {
          const targetHeight = this.panelHeightInputs || currentHeight;
          this.calculatorCard.style.height = `${targetHeight}px`;
          
          // Wait for transition to complete
          setTimeout(() => {
            // Recalculate heights now that we're in input state
            this.calculateHeights();
            
            // Reset transition and clear height constraints
            this.calculatorCard.style.transition = originalTransition;
            this.calculatorCard.style.height = '';
            this.calculatorCard.style.minHeight = originalMinHeight;
            
            this.log('Height reset completed with animation:', {
              actualHeight: this.calculatorCard.offsetHeight,
              panelHeightInputs: this.panelHeightInputs
            });
          }, 300); // Match transition duration
        });
      } else {
        // Result panel is not showing - just reset normally
        // Show input panel, hide result panel
        this.toggleCalculatorState(false);
        
        // Clear height immediately
        this.calculatorCard.style.height = '';
        
        // Force a reflow
        void this.calculatorCard.offsetHeight;
        
        // Recalculate heights now that we're in input state
        this.calculateHeights();
        
        // Set to input height
        if (this.panelHeightInputs) {
          this.calculatorCard.style.height = `${this.panelHeightInputs}px`;
        }
        
        this.log('Height reset completed:', {
          newHeight: this.calculatorCard.style.height,
          actualHeight: this.calculatorCard.offsetHeight,
          panelHeightInputs: this.panelHeightInputs
        });
      }
    } else {
      // No calculator card - just toggle state
      this.toggleCalculatorState(false);
    }
    
    this.log('Calculator reset for new variant');
  }

  calculatePanels(event) {
    // Only handle clicks on our specific button - don't interfere with other elements
    if (event && event.target !== this.calculateButton && !this.calculateButton?.contains(event.target)) {
      return;
    }
    
    // Don't prevent default or stop propagation - let Bootstrap handle events normally
    // The button has type="button" so it won't submit forms anyway
    this.log('Calculate panels method called');
    
    // Ensure we have valid elements - reinitialize if needed
    if (!this.calculateButton || !this.widthInput || !this.quantityRequired) {
      this.log('Missing required elements, reinitializing...');
      this.initializeElements();
      if (!this.calculateButton || !this.widthInput || !this.quantityRequired) {
        console.error(this.logPrefix, 'Required elements still missing after reinitialization');
        return;
      }
    }
    
    // Double-check elements are still in the DOM (they might have been replaced)
    if (!this.contains(this.calculateButton) || !this.contains(this.widthInput)) {
      this.log('Elements are no longer in DOM, reinitializing...');
      this.initializeElements();
      if (!this.calculateButton || !this.widthInput || !this.quantityRequired) {
        console.error(this.logPrefix, 'Required elements missing after DOM check');
        return;
      }
    }

    // Extract numeric value from data attribute (remove "mm" suffix if present)
    // Try dataset first, then fallback to getAttribute for compatibility
    const rawWidthData = this.calculateButton?.dataset?.variantWallpanelWidth || 
                         this.calculateButton?.getAttribute('data-variant-wallpanel-width') || 
                         String(this.DEFAULT_PANEL_WIDTH);
    const maxPanelWidth = parseFloat(rawWidthData.replace('mm', '')) || this.DEFAULT_PANEL_WIDTH;
    const inputValue = parseFloat(this.widthInput.value);

    this.log('Calculate panels:', {
      rawWidthData,
      maxPanelWidth,
      inputValue,
      buttonData: this.calculateButton?.dataset.variantWallpanelWidth,
      elementsValid: {
        calculateButton: !!this.calculateButton,
        widthInput: !!this.widthInput,
        quantityRequired: !!this.quantityRequired
      }
    });

    // Validate input
    if (!inputValue || isNaN(inputValue) || inputValue <= 0) {
      this.showError('Please enter a valid width greater than 0');
      return;
    }
    
    if (inputValue < this.MIN_WIDTH || inputValue > this.MAX_WIDTH) {
      this.showError(`Please enter a width between ${this.MIN_WIDTH} and ${this.MAX_WIDTH} mm`);
      return;
    }

    // Calculate number of panels
    const numberOfPanels = Math.ceil(inputValue / maxPanelWidth);
    
    if (this.quantityRequired) {
      this.quantityRequired.textContent = numberOfPanels;
      // Update ARIA live region for screen readers
      if (!this.quantityRequired.hasAttribute('aria-live')) {
        this.quantityRequired.setAttribute('aria-live', 'polite');
      }
    }

    // Show results first, then calculate and set height with smooth animation
    this.toggleCalculatorState(true);
    
    // Recalculate height now that result panel is visible
    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(() => {
      if (this.calculatorCard) {
        // Get current height before expanding
        const currentHeight = this.calculatorCard.offsetHeight;
        
        // Temporarily clear height to get natural height
        this.calculatorCard.style.height = '';
        
        // Force a reflow
        void this.calculatorCard.offsetHeight;
        
        // Get the natural height of the card with result panel visible
        const naturalHeight = this.calculatorCard.offsetHeight;
        
        // Set initial height to current height for smooth transition
        this.calculatorCard.style.height = `${currentHeight}px`;
        
        // Force a reflow before starting animation
        void this.calculatorCard.offsetHeight;
        
        // Add transition for smooth height change
        const originalTransition = this.calculatorCard.style.transition;
        this.calculatorCard.style.transition = 'height 0.3s ease-in-out';
        
        // Animate to target height using height
        requestAnimationFrame(() => {
          this.calculatorCard.style.height = `${naturalHeight}px`;
          
          // Update stored height for future use
          this.panelHeightResults = naturalHeight;
          
          // Reset transition after animation completes
          setTimeout(() => {
            this.calculatorCard.style.transition = originalTransition;
            // Remove height constraint to allow collapsible content to expand naturally
            // Don't set min-height as it prevents collapse
            this.calculatorCard.style.height = '';
            this.log('Height set for result panel:', {
              naturalHeight,
              panelHeightResults: this.panelHeightResults
            });
          }, 300); // Match transition duration
        });
      }
    });
    
    this.hideError();
    
    this.log('Calculation completed:', numberOfPanels, 'panels needed');
  }

  hideResultsPanel() {
    if (this.quantityRequired) {
      this.quantityRequired.textContent = '0';
    }
    
    if (this.calculatorCard) {
      // Get current height while result panel is still visible
      const currentHeight = this.calculatorCard.offsetHeight;
      
      // Ensure we have the input panel height
      if (!this.panelHeightInputs) {
        // Temporarily show input panel to measure
        const wasInputHidden = this.calculatorInputs?.classList.contains('hidden');
        if (this.calculatorInputs) {
          this.calculatorInputs.classList.remove('hidden');
          this.calculatorInputs.classList.add('active');
        }
        
        // Hide result panel temporarily
        if (this.calculatorResult) {
          this.calculatorResult.classList.add('hidden');
          this.calculatorResult.classList.remove('active');
        }
        
        // Clear height and measure
        this.calculatorCard.style.height = '';
        this.calculatorCard.style.minHeight = '';
        void this.calculatorCard.offsetHeight; // Force reflow
        this.panelHeightInputs = this.calculatorCard.offsetHeight;
        
        // Restore state for animation
        if (this.calculatorInputs) {
          if (wasInputHidden) {
            this.calculatorInputs.classList.add('hidden');
            this.calculatorInputs.classList.remove('active');
          } else {
            this.calculatorInputs.classList.remove('hidden');
            this.calculatorInputs.classList.add('active');
          }
        }
        if (this.calculatorResult) {
          this.calculatorResult.classList.remove('hidden');
          this.calculatorResult.classList.add('active');
        }
      }
      
      // Set the target height
      const targetHeight = this.panelHeightInputs || currentHeight;
      
      // Set transition and current height BEFORE hiding panels
      const originalTransition = this.calculatorCard.style.transition;
      const originalMinHeight = this.calculatorCard.style.minHeight;
      this.calculatorCard.style.transition = 'height 0.3s ease-in-out';
      this.calculatorCard.style.minHeight = '';
      this.calculatorCard.style.height = `${currentHeight}px`;
      
      // Force a reflow
      void this.calculatorCard.offsetHeight;
      
      // Now hide result panel and show input panel
      this.toggleCalculatorState(false);
      
      // Use requestAnimationFrame for smooth animation
      requestAnimationFrame(() => {
        // Animate to target height
        this.calculatorCard.style.height = `${targetHeight}px`;
        
        // Wait for transition to complete, then reset
        setTimeout(() => {
          // Reset transition and clear height constraints
          this.calculatorCard.style.transition = originalTransition;
          this.calculatorCard.style.height = '';
          this.calculatorCard.style.minHeight = originalMinHeight;
          
          this.log('Collapse animation completed');
        }, 300); // Match transition duration
      });
    } else {
      // Fallback if calculatorCard is not available
      this.toggleCalculatorState(false);
    }
  }

  toggleCalculatorState(showResult) {
    if (this.calculatorResult && this.calculatorInputs) {
      this.calculatorResult.classList.toggle("active", showResult);
      this.calculatorResult.classList.toggle("hidden", !showResult);
      this.calculatorInputs.classList.toggle("active", !showResult);
      this.calculatorInputs.classList.toggle("hidden", showResult);
    }
  }
}

export default WallpanelCalculator;
